/** Text in all three supported app languages. Italian is no longer supported. */
export interface LocalizedString {
    de: string;
    en: string;
    fr: string;
}

export type SupportedLang = 'de' | 'en' | 'fr';

/** Picks the right text for the active app language with EN fallback. */
export function pickLang(value: LocalizedString | undefined | null, lang: string): string {
    if (value == null) return '';
    const normalized: SupportedLang = lang.startsWith('de') ? 'de' : lang.startsWith('fr') ? 'fr' : 'en';
    return value[normalized] || value.en || value.de || value.fr || '';
}

export interface AiAgentSettingEntry {
    taskType: string;
    displayName: LocalizedString;
    infoText: LocalizedString;
    enabled: boolean;
    /** Per-agent AGB version (used by future per-agent custom AGBs). */
    version: number;
}

/** Current master AGB record — text + version, displayed in the acceptance modal. */
export interface MasterAgb {
    text: LocalizedString;
    version: number;
}

/**
 * REV-21869 (N-2.3) — how the server reads a service bundle on an org's row. `'PARTIAL'` exists so
 * an incompletely-set bundle is never read as `'ON'` (all paid) nor as `'OFF'` (nothing running,
 * nothing costing) — the second lie is the expensive one.
 */
export type ServiceConsent = 'ON' | 'OFF' | 'PARTIAL';

/**
 * REV-21869 (N-2.3) — a service TILE as the server declares it: id, name, the paid model calls it
 * bundles (`models`), the no-model-call switches of the same service (`operatingModes`, never part
 * of `models`), and the read state of `models` on this org's row. The FE renders from this; it does
 * not keep a catalogue or a member list of its own.
 */
export interface AiServiceEntry {
    serviceId: string;
    displayName: LocalizedString;
    /** The paid model calls this service bundles. Set atomically, all-or-nothing, by one write. */
    models: string[];
    /** Switches of the same service that cost no model call — the bundle write never touches these. */
    operatingModes: string[];
    /** `'ON'` only when every member of `models` is enabled; `'OFF'` only when none is. */
    consent: ServiceConsent;
}

export interface OrgAiSettings {
    organizationId: string;
    dailyTokenLimit: number | null;
    monthlyTokenLimit: number | null;
    /** Tokens already consumed today / this month — for the spending display. */
    currentDailyTokens: number;
    currentMonthlyTokens: number;
    /** Master toggle: when true and acceptedVersion === masterAgb.version, all agents enabled. */
    aiFeaturesEnabled: boolean;
    aiFeaturesAcceptedVersion: number | null;
    masterAgb: MasterAgb;
    /** Per-agent records (informational + future per-agent custom AGBs). */
    agents: AiAgentSettingEntry[];
    /**
     * REV-21869 (N-2.3) — the service catalogue, each with its members and read state. Optional:
     * ABSENT on an AI service older than this ticket's wave 1, and the FE must survive that (render
     * no catalogue, not throw).
     */
    services?: AiServiceEntry[];
}

/** Whether the master switch covers every AI task: on, and accepted for the current master AGB. */
export function masterCovers(settings: {
    readonly aiFeaturesEnabled?: boolean;
    readonly aiFeaturesAcceptedVersion?: number | null;
    readonly masterAgb?: { readonly version: number };
}): boolean {
    return (
        settings.aiFeaturesEnabled === true &&
        settings.masterAgb !== undefined &&
        settings.aiFeaturesAcceptedVersion === settings.masterAgb.version
    );
}

/** Body for PATCH /ai-features (the master toggle). */
export interface SetAiFeaturesRequest {
    enabled: boolean;
    /** REQUIRED when enabling — must equal masterAgb.version. */
    acceptedVersion?: number;
}
